import { NodeDetailDrawer } from '../DrawingBoard/NodeDetailDrawer'
import { ChecklistDetailDialog } from '../ChecklistBoard/ChecklistDetailDialog'
import type { ChecklistItem, ChecklistMemberOption, ChecklistWorkflowStatus } from '../../types/schema'
import type { ChecklistBaseOperationalPermissions } from '../../utils/checklistOperationalPermissions'
import type { useRecurringLens } from '../../context/recurringLensContextCore'

export interface BoardDrawersProps {
  draftDrawerNode: ChecklistItem | null
  setDraftDrawerNode: (node: ChecklistItem | null) => void
  displayNodes: ChecklistItem[]
  workspaceMembers: ChecklistMemberOption[]
  baseOperationalPermissions: ChecklistBaseOperationalPermissions
  persistDraftChecklist: (node: ChecklistItem) => Promise<void>
  drawerState: string | null
  selectedNode: ChecklistItem | null
  recurringLens: ReturnType<typeof useRecurringLens>
  closeChecklistNode: () => void
  lockedNodeIds: Set<string>
  canInteractWithChecklist: boolean
  updateTitle: (node: ChecklistItem, title: string) => Promise<void>
  updateWeightage: (node: ChecklistItem, weightage: string) => Promise<void>
  updateNodeVerification: (node: ChecklistItem, updates: Partial<ChecklistItem>, notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null) => Promise<void>
  updateNodeStatus: (node: ChecklistItem, status: ChecklistWorkflowStatus, opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null }) => Promise<void>
  updateNodeAssignment: (node: ChecklistItem, assignee: ChecklistMemberOption | null, opts?: { scope?: 'single' | 'future' | 'global' | null; pageKey?: string | null }) => Promise<void>
  selectedNodeOperationalAccess: any
  canPerformOnNode: (permission: any, nodeId: string) => boolean
  canEditStructure: boolean
  canDeleteChecklist: boolean
  deleteNode: (node: ChecklistItem) => Promise<void>
}

export function BoardDrawers({
  draftDrawerNode,
  setDraftDrawerNode,
  displayNodes,
  workspaceMembers,
  baseOperationalPermissions,
  persistDraftChecklist,
  drawerState,
  selectedNode,
  recurringLens,
  closeChecklistNode,
  lockedNodeIds,
  canInteractWithChecklist,
  updateTitle,
  updateWeightage,
  updateNodeVerification,
  updateNodeStatus,
  updateNodeAssignment,
  selectedNodeOperationalAccess,
  canPerformOnNode,
  canEditStructure,
  canDeleteChecklist,
  deleteNode,
}: BoardDrawersProps) {
  return (
    <>
      {/* Draft drawer — mounted only while a popup-first create is in flight.
        * Rendered before the regular detail drawer so a click on this drawer's
        * overlay is captured here, not on the (also-open-eligible) node drawer
        * behind it. The draft drawer's onCreate persists the item and closes;
        * onClose without saving simply drops the placeholder. */}
      {draftDrawerNode && (
        <NodeDetailDrawer
          isOpen
          node={draftDrawerNode}
          presentation="popup"
          boardNodes={displayNodes}
          workspaceMembers={workspaceMembers}
          isLocked={false}
          isReadOnly={false}
          onClose={() => setDraftDrawerNode(null)}
          onTitleBlur={async () => undefined}
          onWeightageBlur={async () => undefined}
          onVerificationChange={async () => undefined}
          onStatusChange={async () => undefined}
          onAssignmentChange={async () => undefined}
          operationalPermissions={{
            ...baseOperationalPermissions,
            complete: false,
          }}
          operationalDisabledReason={null}
          completionDisabledReason={null}
          canEdit
          canDelete={false}
          onDeleteNode={async () => undefined}
          isNewNode
          onCreate={persistDraftChecklist}
          newNodeSaveLabel="Save & add to canvas"
        />
      )}
      {/* Recurring items open the ChecklistDetailDialog wizard (with its
        * occurrence sidebar showing past / current / upcoming instances) —
        * the regular NodeDetailDrawer is single-instance and hides that
        * mechanism. Non-recurring (MAIN) items keep the existing drawer.
        * `open` is gated on the same drawer-state predicate as before so
        * only one detail surface is ever on screen at a time. */}
      {(() => {
        const detailOpen = drawerState === 'NODE_DETAIL' && Boolean(selectedNode)
        const selectedIsRecurring = selectedNode?.checklistType === 'DAILY' || selectedNode?.checklistType === 'WEEKLY'
        if (selectedIsRecurring && selectedNode) {
          return (
            <ChecklistDetailDialog
              open={detailOpen}
              onDismiss={closeChecklistNode}
              itemId={selectedNode.id}
              mode="view"
              initialOccurrenceKey={recurringLens?.focusedKey ?? null}
              upcomingWindow={recurringLens?.recurringWindowDays ?? 3}
            />
          )
        }
        return (
          <NodeDetailDrawer
            isOpen={detailOpen}
            node={selectedNode}
            presentation="popup"
            boardNodes={displayNodes}
            workspaceMembers={workspaceMembers}
            isLocked={selectedNode ? lockedNodeIds.has(selectedNode.id) : false}
            isReadOnly={!canInteractWithChecklist}
            onClose={closeChecklistNode}
            onTitleBlur={updateTitle}
            onWeightageBlur={updateWeightage}
            onVerificationChange={updateNodeVerification}
            onStatusChange={updateNodeStatus}
            onAssignmentChange={updateNodeAssignment}
            operationalPermissions={selectedNodeOperationalAccess?.permissions ?? {
              ...baseOperationalPermissions,
              complete: false,
            }}
            operationalDisabledReason={selectedNodeOperationalAccess?.workDisabledReason ?? null}
            completionDisabledReason={selectedNodeOperationalAccess?.completionDisabledReason ?? null}
            canEdit={selectedNode ? canPerformOnNode('checklist.edit_structure', selectedNode.id) : canEditStructure}
            canDelete={selectedNode ? canPerformOnNode('checklist.delete', selectedNode.id) : canDeleteChecklist}
            onDeleteNode={deleteNode}
          />
        )
      })()}
    </>
  )
}
